/**
 * Owner in-app manual. Keep in sync with docs/manuals/OWNER_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const OWNER_MANUAL_INTRO = {
  title: "Owner guide",
  subtitle:
    "Organization owners run their practice in Chairside: appointments, consultations, recare, pipeline, team setup, and org settings. Use the main sidebar for day-to-day work; open **Settings** (gear) for organization, locations, and team members. If you have multiple sites, use the **location** control in the **header** so lists and dashboards match the correct office.",
  defaultRoute: "/dashboard",
};

/** @typedef {{ id: string, group: 'menu' | 'settings' | 'more', menuLabel: string, route: string, keywords: string[], summary: string, howToUse: string[] }} OwnerManualSection */

/** @type {OwnerManualSection[]} */
export const OWNER_MANUAL_SECTIONS = [
  {
    id: "dashboard",
    group: "menu",
    menuLabel: "Dashboard",
    route: "/dashboard",
    keywords: ["home", "overview", "stats", "kpi", "practice"],
    summary:
      "Practice home: snapshot of activity, shortcuts, and role-specific widgets for your organization.",
    howToUse: [
      "Click **Dashboard** in the sidebar (`/dashboard`).",
      "If the header shows a **location** switcher, pick the site you want metrics for before reading the numbers.",
      "Review summary cards (patients, consultations, follow-ups, pipeline counts) and open linked sections from the page.",
      "Use appointment and follow-up widgets to jump into same-day work.",
    ],
    faqs: [
      {
        question: "How do I switch location on the dashboard?",
        answer:
          "Use the location dropdown in the top header. Dashboard metrics and lists filter to the selected office.",
      },
    ],
  },
  {
    id: "appointments",
    group: "menu",
    menuLabel: "Appointments",
    route: "/appointments",
    keywords: ["schedule", "calendar", "visits", "pms", "sync", "confirm", "recording"],
    summary:
      "View and work with appointments for your locations, aligned with PMS data where integrated. Launch chairside recording from here.",
    howToUse: [
      "Open **Appointments** (`/appointments`).",
      "Set the **location** in the header and use date filters to find visits.",
      "Open a row to view details, confirm status, or start a **consultation recording** for that appointment.",
      "If PMS is connected, appointments sync from Sikka/PMS — use **Settings → PMS integration** to manage sync.",
    ],
    faqs: [
      {
        question: "How do I start a consultation from an appointment?",
        answer:
          "Open [[Appointments|/appointments]], select the visit, and launch recording (or go to [[Recording|/recording/:appointmentId]]). After recording, review under [[Consultations|/consultations]].",
      },
      {
        question: "How do I sync patients and appointments from PMS?",
        answer:
          "Go to [[PMS integration|/settings/pms]] (owner settings). Connect Sikka credentials and run patient/appointment sync for each location.",
      },
    ],
  },
  {
    id: "consultations",
    group: "menu",
    menuLabel: "Consultations",
    route: "/consultations",
    keywords: [
      "consultation",
      "consultations",
      "recording",
      "visit",
      "clinical",
      "soap",
      "transcript",
      "start",
      "take",
      "download",
      "export",
      "report",
      "excel",
    ],
    summary:
      "Run and review chairside consultations: start recording from Appointments or Recording, then review the list and each consultation record. Export SOAP notes individually or in bulk.",
    howToUse: [
      "To **take** (start) a consultation: open **Appointments** (`/appointments`), find the patient's visit, and launch recording — or go to **Recording** (`/recording`) or `/recording/:appointmentId`.",
      "On the recording wizard, complete consent and microphone checks, then record the consult chairside.",
      "After the visit, open **Consultations** (`/consultations`); click a row for **Consultation detail** (`/consultations/:id`) to review transcript, SOAP notes, and recordings.",
      "To **download one SOAP note**: open a consultation detail page and click **Export SOAP Note** (downloads a `.txt` file).",
      "To **download a SOAP report (bulk Excel)**: on **Consultations** (`/consultations`), set your date range if needed, then click **Download SOAP** (downloads `.xlsx` with SOAP + patient fields for that range).",
    ],
    faqs: [
      {
        question: "How do I take a consultation?",
        answer:
          "Start from Appointments (/appointments) and launch recording for the visit, or open Recording (/recording). Complete consent and device checks, record the consult, then find the record under [[Consultations|/consultations]].",
      },
      {
        question: "How do I start a consultation recording?",
        answer:
          "Use [[Recording|/recording]] or /recording/:appointmentId from an appointment. After recording, open [[Consultations|/consultations]] to review the visit.",
      },
      {
        question: "How do I download a SOAP report?",
        answer:
          "Bulk export: open [[Consultations|/consultations]], pick a date range, click Download SOAP — you get an Excel (.xlsx) file. Single note: open a consultation at /consultations/:id and click Export SOAP Note for a .txt download.",
      },
      {
        question: "How do I export SOAP notes?",
        answer:
          "List export (.xlsx): [[Consultations|/consultations]] → Download SOAP. One consultation (.txt): open the consultation detail → Export SOAP Note.",
      },
    ],
  },
  {
    id: "recare-dashboard",
    group: "menu",
    menuLabel: "Recare Dashboard",
    route: "/recare-dashboard",
    keywords: ["recare", "recall", "hygiene", "retention", "pool", "export", "rebuild"],
    summary:
      "Recare (recall) dashboard: identify patients overdue for D0120 periodic exam recall, monitor buckets, and rebuild or export the recare pool.",
    howToUse: [
      "Choose **Recare Dashboard** (`/recare-dashboard`).",
      "Confirm the correct **location** in the header if your org has multiple sites.",
      "Review overdue buckets (0–6 mo, 6–12 mo, etc.) and KPI cards for your recare pipeline.",
      "Click **Rebuild Recare Pool** to refresh pool data from PMS appointments (owner). Super Admin can **Export Recare Pool** instead.",
      "Use linked recare workflow actions from the dashboard as your office defines.",
    ],
    faqs: [
      {
        question: "What is the recare pool?",
        answer:
          "Patients overdue for D0120 recall based on last completed exam + 6 months, excluding those already booked. View and manage on [[Recare Dashboard|/recare-dashboard]].",
      },
      {
        question: "How do I export the recare pool?",
        answer:
          "Super Admin: [[Recare Dashboard|/recare-dashboard]] → Export Recare Pool. Owners use Rebuild Recare Pool to refresh data.",
      },
    ],
  },
  {
    id: "treatments",
    group: "menu",
    menuLabel: "Treatments",
    route: "/treatments",
    keywords: [
      "treatment",
      "treatments",
      "create",
      "add",
      "catalog",
      "fee",
      "procedure",
      "codes",
      "production",
      "cdt",
    ],
    summary:
      "Configure your org's treatment catalog (names, categories, price ranges) used in pipeline and case presentation. Owner-only sidebar item.",
    howToUse: [
      "Open **Treatments** (`/treatments`) from the sidebar.",
      "Click **Add Treatment** to create a new entry.",
      "Enter treatment name, category, CDT code if used, and price range; click **Create Treatment** to save.",
      "Use the list to edit or organize existing treatments.",
    ],
    faqs: [
      {
        question: "How do I create a treatment?",
        answer:
          "Open [[Treatments|/treatments]], click Add Treatment, fill in name, category, and price details, then save with Create Treatment.",
      },
      {
        question: "How do I add a new treatment?",
        answer:
          "Go to [[Treatments|/treatments]] and use the Add Treatment button to open the form and save your new catalog entry.",
      },
    ],
  },
  {
    id: "pipeline",
    group: "menu",
    menuLabel: "Pipeline",
    route: "/pipeline",
    keywords: ["sales", "cases", "opportunities", "stages", "kanban", "move", "patient"],
    summary:
      "Sales pipeline: kanban view of patients in active treatment plans. Move patients between stages, add notes, and create follow-ups.",
    howToUse: [
      "Go to **Pipeline** (`/pipeline`).",
      "Select a **treatment plan** from the dropdown to load its stages as columns.",
      "Click a patient card to open details, add notes, or move them to another stage (notes are required when changing stage).",
      "Use filters for location, stage, or search to find specific patients.",
      "From a patient card, create a **follow-up** when your workflow requires scheduled outreach.",
    ],
    faqs: [
      {
        question: "How do I move a patient to another pipeline stage?",
        answer:
          "Open [[Pipeline|/pipeline]], click the patient card, pick a new stage in the modal, enter required notes, and save.",
      },
      {
        question: "What is the difference between pipeline and stages?",
        answer:
          "[[Stages|/stages]] defines the steps for a treatment plan. [[Pipeline|/pipeline]] is where you move live patients through those stages day to day.",
      },
    ],
  },
  {
    id: "stages",
    group: "menu",
    menuLabel: "Stages",
    route: "/stages",
    keywords: [
      "stage",
      "stages",
      "treatment stage",
      "treatment stages",
      "pipeline stage",
      "pipeline stages",
      "treatment plan",
      "workflow",
      "create",
      "add",
      "reorder",
    ],
    summary:
      "Define pipeline stages for each treatment plan (e.g. Recare, implant cases). Owner-only. Not the same as the Treatments catalog.",
    howToUse: [
      "Open **Stages** (`/stages`) from the sidebar.",
      "Select the **treatment plan** you want to configure (you need at least one treatment plan created first).",
      "Click **Add Stage**, enter stage name and description, set order, and save.",
      "Drag or edit stages to reorder; system default Recare stages cannot be deleted.",
    ],
    faqs: [
      {
        question: "How do I create a treatment stage?",
        answer:
          "Open [[Stages|/stages]], select a treatment plan, click Add Stage, fill in name/description/order, and save. This is pipeline staging — not the Treatments catalog at /treatments.",
      },
      {
        question: "How do I add a pipeline stage?",
        answer:
          "Go to [[Stages|/stages]], pick a treatment plan, then use Add Stage to create a new step in that plan's pipeline.",
      },
      {
        question: "Can I delete a stage?",
        answer:
          "Yes for custom stages: [[Stages|/stages]] → delete (soft delete; Super Admin can restore from [[Recycle Bin|/recycle-bin]]). Default Recare stages cannot be deleted.",
      },
    ],
  },
  {
    id: "follow-ups",
    group: "menu",
    menuLabel: "Follow-Ups",
    route: "/follow-ups",
    keywords: ["tasks", "outreach", "messages", "cadence", "calendar", "overdue", "complete"],
    summary:
      "Follow-up calendar: scheduled patient outreach from pipeline, recare, and AI workflows. View overdue items and complete or reschedule.",
    howToUse: [
      "Open **Follow-Ups** (`/follow-ups`).",
      "Use the calendar to pick a date; overdue follow-ups appear at the top in red.",
      "Click a follow-up row to open details, mark **Complete**, or adjust the schedule.",
      "Click **Refresh** to reload the latest list from the server.",
    ],
    faqs: [
      {
        question: "How do I complete a follow-up?",
        answer:
          "Open [[Follow-Ups|/follow-ups]], find the item (or use the overdue banner), open it, and mark it complete with any required notes.",
      },
      {
        question: "Where do follow-ups come from?",
        answer:
          "They are created from [[Pipeline|/pipeline]], recare workflows, reactivation, or AI-assisted outreach — all appear on the follow-up calendar.",
      },
    ],
  },
  {
    id: "support",
    group: "menu",
    menuLabel: "Support",
    route: "/tickets",
    keywords: ["ticket", "help", "issue", "support", "bug", "request"],
    summary:
      "Organization support tickets: create and track requests to your success or product channel.",
    howToUse: [
      "Choose **Support** (`/tickets`).",
      "Click **New Ticket**, enter subject and description with steps to reproduce.",
      "Track status on existing threads; reply when the team asks for more detail.",
    ],
    faqs: [
      {
        question: "How do I open a support ticket?",
        answer:
          "Go to [[Support|/tickets]] → New Ticket, describe the issue clearly, and submit. Use this when Help Assistant cannot answer or you hit a bug.",
      },
    ],
  },
  {
    id: "settings",
    group: "settings",
    menuLabel: "Settings (hub)",
    route: "/settings",
    keywords: ["gear", "configuration", "practice"],
    summary:
      "Settings landing: cards for organization profile, locations, team members, and profile shortcut.",
    howToUse: [
      "Click the **Settings** (gear) item at the bottom of the sidebar (`/settings`).",
      "Pick a card to drill into **Organization**, **Locations**, or **Team Members**.",
      "Use **Edit profile** on the profile card for `/settings/profile`.",
    ],
  },
  {
    id: "settings-organization",
    group: "settings",
    menuLabel: "Organization settings",
    route: "/settings/organization",
    keywords: ["name", "branding", "practice info", "delete org"],
    summary:
      "Legal and display settings for your organization; destructive actions (if shown) require confirmation.",
    howToUse: [
      "From **Settings**, open **Organization** (`/settings/organization`).",
      "Update fields your role is allowed to change; save and verify downstream displays.",
    ],
  },
  {
    id: "settings-locations",
    group: "settings",
    menuLabel: "Locations",
    route: "/settings/locations",
    accessLevel: "view",
    keywords: ["sites", "address", "timezone", "pms location", "view", "create location"],
    summary:
      "View practice locations (names, time zones, PMS connection status). Owners cannot create, edit, or delete locations.",
    howToUse: [
      "Open Locations from Settings (/settings/locations).",
      "Review your organization's sites, time zones, and integration status (view only).",
      "To add a new location, contact your Super Admin / platform team — they create locations.",
    ],
    faqs: [
      {
        question: "Who can create a location?",
        answer:
          "Only Super Admin can create, edit, or delete locations. Owners can view the list at [[Locations|/settings/locations]] but cannot add new sites. Ask your Super Admin or open [[Support Tickets|/tickets]] if you need a new office added.",
      },
      {
        question: "Can owner create a location?",
        answer:
          "No. Owner is view-only on Locations. Super Admin creates locations via Admin → [[Locations|/settings/locations]] → Add Location.",
      },
    ],
  },
  {
    id: "settings-users",
    group: "settings",
    menuLabel: "Team Members",
    route: "/settings/users",
    keywords: ["staff", "tc", "front desk", "invite", "roles"],
    summary:
      "Create and manage org users — assign roles such as TC (treatment coordinator) or front desk and link locations (per your RBAC).",
    howToUse: [
      "Go to **Team Members** (`/settings/users`).",
      "Click **Add User**, enter name and email, assign role (Treatment Coordinator, Front Desk, etc.), and link locations.",
      "Edit or deactivate accounts when staff leave; deactivated users cannot sign in.",
    ],
    faqs: [
      {
        question: "How do I add a treatment coordinator?",
        answer:
          "[[Team Members|/settings/users]] → Add User → choose Treatment Coordinator role and assign their location(s).",
      },
      {
        question: "How do I add a team member?",
        answer:
          "Open [[Team Members|/settings/users]], click Add User, set role and locations, then save.",
      },
    ],
  },
  {
    id: "settings-pms",
    group: "settings",
    menuLabel: "PMS integration",
    route: "/settings/pms",
    keywords: ["sikka", "integration", "connector", "sync", "patients", "appointments"],
    summary:
      "Practice management system connection: Sikka credentials, patient sync, and appointment sync per location.",
    howToUse: [
      "Navigate to **PMS integration** (`/settings/pms`) from Settings or a direct link.",
      "Enter Sikka office credentials for each location and save.",
      "Run **Sync Patients** and **Sync Appointments** to pull data into Chairside.",
      "Verify sync status; contact support if tokens expire or sync fails.",
    ],
    faqs: [
      {
        question: "How do I sync patients from PMS?",
        answer:
          "Go to [[PMS integration|/settings/pms]], connect Sikka for the location, then run patient sync. Appointments sync separately on the same page.",
      },
      {
        question: "How do I connect Sikka?",
        answer:
          "Owner: [[PMS integration|/settings/pms]] → enter office ID and credentials per location → save and test sync.",
      },
    ],
  },
  {
    id: "settings-profile",
    group: "settings",
    menuLabel: "Profile",
    route: "/settings/profile",
    keywords: ["password", "avatar", "email", "title"],
    summary:
      "Your personal account: name, contact, password, and optional professional title.",
    howToUse: [
      "From **Settings**, click **Edit profile** on the profile card, or open `/settings/profile` directly.",
      "Update fields and save; use your org’s password policy for changes.",
    ],
  },
  {
    id: "smile-preview",
    group: "more",
    menuLabel: "Smile Preview",
    route: "/smile-preview",
    keywords: ["ai", "cosmetic", "image", "smile", "transformation", "upload"],
    summary:
      "AI-assisted smile visualization: upload a patient photo and generate a cosmetic preview for case conversations.",
    howToUse: [
      "Open **Smile Preview** (`/smile-preview`).",
      "Upload or select a patient photo following on-page guidance.",
      "Generate the preview and review before sharing in a consult conversation.",
    ],
    faqs: [
      {
        question: "How do I create a smile preview?",
        answer:
          "Go to [[Smile Preview|/smile-preview]], upload a photo, and run the AI generation. Owner and Treatment Coordinator roles have access.",
      },
    ],
  },
  {
    id: "recording",
    group: "more",
    menuLabel: "Recording",
    route: "/recording",
    keywords: ["chairside", "capture", "consent", "microphone", "pause", "stop", "consultation"],
    summary:
      "Chairside recording wizard: consent, device checks, live capture, pause/resume, and stop to create a consultation.",
    howToUse: [
      "Use `/recording` or `/recording/:appointmentId` when launching from an appointment.",
      "Complete **consent** prompts and **microphone** checks before recording.",
      "Press record; use **Pause** if needed, then **Stop** when the consult ends.",
      "After stopping, review the visit under [[Consultations|/consultations]].",
    ],
    faqs: [
      {
        question: "How do I record a consultation?",
        answer:
          "From [[Appointments|/appointments]] launch recording, or open [[Recording|/recording]]. Complete consent and mic checks, record, then stop. The consultation appears under [[Consultations|/consultations]].",
      },
    ],
  },
  {
    id: "coaching",
    group: "more",
    menuLabel: "Coaching",
    route: "/coaching",
    keywords: ["scorecard", "feedback", "consultation review", "quality"],
    summary:
      "Consultation coaching and scorecards: review AI-generated feedback on recorded consultations for your team.",
    howToUse: [
      "Open **Coaching** (`/coaching`).",
      "Browse scorecards tied to consultation recordings.",
      "Use insights for TC training and case acceptance improvement.",
    ],
    faqs: [
      {
        question: "Where does coaching data come from?",
        answer:
          "Scorecards are generated from [[Consultations|/consultations]] recordings. Open [[Coaching|/coaching]] to review team or personal feedback.",
      },
    ],
  },
  {
    id: "analytics",
    group: "more",
    menuLabel: "Analytics",
    route: "/analytics",
    keywords: ["metrics", "kpi", "dashboard", "reports", "performance", "stats"],
    summary:
      "Practice analytics: KPIs for consultations, pipeline, follow-ups, and operational metrics for your organization.",
    howToUse: [
      "Open **Analytics** (`/analytics`).",
      "Set date range and location filters as provided.",
      "Review charts and export or drill into linked modules when available.",
    ],
    faqs: [
      {
        question: "How do I view practice analytics?",
        answer:
          "Owners open [[Analytics|/analytics]] (not in the main sidebar — use this manual link or bookmark). Filter by date and location.",
      },
    ],
  },
  {
    id: "reactivation",
    group: "more",
    menuLabel: "Reactivation",
    route: "/reactivation",
    keywords: ["inactive", "dormant", "campaign", "win-back", "patients"],
    summary:
      "Patient reactivation: identify inactive patients and launch win-back outreach campaigns that create follow-ups.",
    howToUse: [
      "Open **Reactivation** (`/reactivation`).",
      "Review inactive patient cohorts and campaign options.",
      "Launch a campaign; resulting outreach appears in [[Follow-Ups|/follow-ups]].",
      "For hygiene recall (D0120), use [[Recare Dashboard|/recare-dashboard]] instead — reactivation targets dormant patients.",
    ],
    faqs: [
      {
        question: "What is the difference between Recare and Reactivation?",
        answer:
          "[[Recare Dashboard|/recare-dashboard]] = hygiene recall (D0120 overdue). [[Reactivation|/reactivation]] = win-back for inactive/dormant patients. Both can create [[Follow-Ups|/follow-ups]].",
      },
    ],
  },
  createHelpAssistantManualSection(),
];

export const OWNER_MANUAL_GROUPS = {
  menu: {
    label: "Main menu",
    description:
      "Primary sidebar items for owners (plus this manual).",
  },
  settings: {
    label: "Settings",
    description:
      "Organization configuration and your profile (gear icon at bottom of sidebar).",
  },
  more: {
    label: "More features",
    description:
      "Routes you can use with owner permissions that are not shown as primary sidebar items.",
  },
};

export function filterOwnerManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return OWNER_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
